/**
 * Reassurance Manager - Moon Moon Agency
 * Carrousel mobile (scroll-snap natif) + dots générés dynamiquement pour la
 * section MM-Réassurance. Aucune dépendance externe.
 */

/*********************************************************************************************
 * 🔧 REASSURANCE MANAGER
 */
const ReassuranceManager = {
	config: {
		selectors: {
			carousel: '[data-mm-reassurance-carousel]',
			track: '[data-mm-reassurance-track]',
			dots: '[data-mm-reassurance-dots]',
		},
		classes: {
			activeDot: 'is-active',
		},
	},

	init(root = document) {
		const carousels = root.querySelectorAll(this.config.selectors.carousel);
		if (!carousels.length) return;

		carousels.forEach((carousel) => this._setupCarousel(carousel));
		window.log('info', '🔧 [REASSURANCE]', 'Initialized', { count: carousels.length });
	},

	_setupCarousel(carousel) {
		const track = carousel.querySelector(this.config.selectors.track);
		const dotsWrap = carousel.querySelector(this.config.selectors.dots);
		if (!track) return;

		const items = Array.from(track.children);
		if (items.length < 2) return;

		let current = -1;
		const goTo = (index) => {
			track.scrollTo({ left: items[index].offsetLeft - items[0].offsetLeft, behavior: 'smooth' });
		};

		/* Génère un dot par item */
		const dots = dotsWrap
			? items.map((_, index) => {
					const dot = document.createElement('button');
					dot.type = 'button';
					dot.className = 'mm-reassurance__dot';
					dot.setAttribute('role', 'tab');
					dot.setAttribute('aria-label', `${index + 1} / ${items.length}`);
					dot.addEventListener('click', () => goTo(index));
					dotsWrap.appendChild(dot);
					return dot;
				})
			: [];

		const setActive = (index) => {
			if (index === current) return;
			current = index;
			dots.forEach((dot, i) => {
				const active = i === index;
				dot.classList.toggle(this.config.classes.activeDot, active);
				dot.setAttribute('aria-selected', String(active));
			});
		};
		setActive(0);

		const itemObserver = new IntersectionObserver(() => {
			const step = items[1].offsetLeft - items[0].offsetLeft;
			if (step > 0) setActive(Math.min(items.length - 1, Math.round(track.scrollLeft / step)));
		}, { root: track, threshold: 0.5 });
		items.forEach((item) => itemObserver.observe(item));

		this._setupAutoplay(carousel, track, items.length, goTo, () => current);
	},

	_setupAutoplay(carousel, track, count, goTo, getCurrent) {
		const delay = parseInt(carousel.dataset.autoplay, 10) * 1000;
		if (!delay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		const hold = { hidden: true, idle: true, touch: false, mouse: false, focus: false };
		let timer = null;

		const schedule = () => {
			window.clearTimeout(timer);
			if (Object.values(hold).some(Boolean)) return;
			timer = window.setTimeout(() => {
				if (track.scrollWidth > track.clientWidth) goTo((getCurrent() + 1) % count);
				schedule();
			}, delay);
		};
		const setHold = (reason, value) => {
			hold[reason] = value;
			schedule();
		};

		carousel.addEventListener('touchstart', () => setHold('touch', true), { passive: true });
		carousel.addEventListener('touchend', () => setHold('touch', false));
		carousel.addEventListener('touchcancel', () => setHold('touch', false));
		carousel.addEventListener('pointerenter', (e) => {
			if (e.pointerType === 'mouse') setHold('mouse', true);
		});
		carousel.addEventListener('pointerleave', (e) => {
			if (e.pointerType === 'mouse') setHold('mouse', false);
		});
		carousel.addEventListener('focusin', (e) => {
			if (e.target.matches(':focus-visible')) setHold('focus', true);
		});
		carousel.addEventListener('focusout', (e) => {
			if (!carousel.contains(e.relatedTarget)) setHold('focus', false);
		});

		const gestures = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];
		const onFirstGesture = () => {
			gestures.forEach((type) => window.removeEventListener(type, onFirstGesture));
			setHold('idle', false);
		};
		gestures.forEach((type) => window.addEventListener(type, onFirstGesture, { passive: true }));

		new IntersectionObserver(([entry]) => setHold('hidden', !entry.isIntersecting), { threshold: 0.5 }).observe(carousel);
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => ReassuranceManager.init());
} else {
	ReassuranceManager.init();
}

document.addEventListener('shopify:section:load', (e) => ReassuranceManager.init(e.target));
